<template>
    <div    class="w-full flex flex-col gap-4">
        <h2>Date Range Picker</h2>

        <div    class="flex flex-wrap items-center gap-6">
            <label  class="flex items-center gap-2">
                <span>Timezone</span>
                <select v-model="timezone"
                        class="rounded-md border border-gray-300 px-2 py-1"
                >
                    <optgroup label="Companies">
                        <option v-for="company in companies"
                                :key="company.id"
                                :value="company.timezone"
                        >{{ company.name }} ({{ company.timezone }})</option>
                    </optgroup>
                    <!-- Opposite ends of the clock: one is always on a different date than the browser. -->
                    <optgroup label="Test zones">
                        <option value="Pacific/Kiritimati">Pacific/Kiritimati (UTC+14)</option>
                        <option value="Pacific/Pago_Pago">Pacific/Pago_Pago (UTC-11)</option>
                    </optgroup>
                </select>
            </label>

            <label  class="flex items-center gap-2">
                <input  type="checkbox"
                        v-model="disableFutureDates"
                >
                <span>Disable future dates</span>
            </label>

            <label  class="flex items-center gap-2">
                <input  type="checkbox"
                        v-model="use12Hour"
                >
                <span>12-hour times</span>
            </label>

            <pre    class="rounded-md bg-gray-100 px-3 py-1 text-sm">{{ nowLabel }}</pre>
        </div>

        <div    v-for="example in examples"
                :key="example.key"
                class="flex flex-col gap-2"
        >
            <h3 class="font-semibold">{{ example.title }}</h3>
            <code   class="text-sm text-gray-600">{{ example.usage }}</code>
            <div    class="flex flex-wrap items-start gap-6">
                <div    class="w-[38rem]">
                    <date-range-picker  v-model="values[example.key]"
                                        :mode="example.mode"
                                        :presets="example.presets"
                                        :enable-time="example.enableTime ?? false"
                                        :time-format="use12Hour ? '12h' : '24h'"
                                        :timezone="timezone"
                                        :disable-future-dates="disableFutureDates"
                    ></date-range-picker>
                </div>
                <pre    class="rounded-md bg-gray-100 p-3 text-sm">{{ JSON.stringify(values[example.key], null, 2) }}</pre>
            </div>
        </div>
    </div>
</template>

<script>
import { DateTime } from 'luxon'
import { CUSTOM, LAST_7_DAYS, MONTH, TODAY, YESTERDAY, lastNDays } from '../../../utils/dateRangePresets'

export default {
    name: 'DateRangePickerTest',
    props: {
        // [{ id, name, timezone }] for the companies the user can access.
        companies: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            disableFutureDates: true,
            use12Hour: false,
            timezone: this.companies[0]?.timezone ?? null,
            values: {
                defaults: null,
                single: null,
                custom: null,
                bare: null,
                rangeTime: null,
                singleTime: null,
            },
            examples: [
                {
                    key: 'defaults',
                    title: 'Default presets (the Figma)',
                    usage: '<date-range-picker v-model="range" :timezone="company.timezone" disable-future-dates>',
                    mode: 'range',
                    presets: null,
                },
                {
                    key: 'single',
                    title: 'Single day',
                    usage: '<date-range-picker v-model="day" mode="single">',
                    mode: 'single',
                    presets: null,
                },
                {
                    key: 'custom',
                    title: 'Custom preset list',
                    usage: ':presets="[TODAY, YESTERDAY, LAST_7_DAYS, lastNDays(30), lastNDays(90), { key: \'fiscalQ1\', ... }, MONTH, CUSTOM]"',
                    mode: 'range',
                    presets: [
                        TODAY,
                        YESTERDAY,
                        LAST_7_DAYS,
                        lastNDays(30),
                        lastNDays(90),
                        // A fixed (non-relative) preset defined right here on the page.
                        {
                            key: 'fiscalQ1',
                            label: 'Fiscal Q1 (Jul - Sep)',
                            range: today => {
                                const year = (today.month >= 7) ? today.year : today.year - 1
                                return {
                                    start: DateTime.fromObject({ year, month: 7, day: 1 }),
                                    end: DateTime.fromObject({ year, month: 9, day: 30 }),
                                }
                            },
                        },
                        MONTH,
                        CUSTOM,
                    ],
                },
                {
                    key: 'bare',
                    title: 'Calendar only (no presets)',
                    usage: '<date-range-picker v-model="range" :presets="[]">',
                    mode: 'range',
                    presets: [],
                },
                {
                    key: 'rangeTime',
                    title: 'Range with times',
                    usage: '<date-range-picker v-model="range" enable-time time-format="24h">',
                    mode: 'range',
                    presets: null,
                    enableTime: true,
                },
                {
                    key: 'singleTime',
                    title: 'Single day with a time',
                    usage: '<date-range-picker v-model="moment" mode="single" enable-time>',
                    mode: 'single',
                    presets: null,
                    enableTime: true,
                },
            ],
        }
    },
    computed: {
        // Shows how far the selected zone's "today" can sit from the browser's.
        nowLabel() {
            const browser = DateTime.now()
            const zoned = this.timezone ? browser.setZone(this.timezone) : browser
            return `Browser: ${browser.toFormat('LLL d HH:mm ZZZZ')}   Selected: ${zoned.toFormat('LLL d HH:mm ZZZZ')}`
        },
    },
}
</script>
